<template>
  <v-footer white id="footer">
    <v-container fluid white class="px-5">
      <v-layout row wrap justify-center>
        <v-flex class="xs12 sm6 md2">
          <v-list dense>
            <v-subheader
              class="subheading font-weight-bold"
              style="height: 25px"
            >
              {{ $t('footer.cities') }}
            </v-subheader>
            <template v-for="city in cities">
              <v-list-tile
                :key="city.code"
                :disabled="disableCity(city.code)"
                :href="city.web"
                :class="{ 'ml-2': $vuetify.breakpoint.smAndDown }"
              >
                <v-list-tile-content>
                  <v-list-tile-title>
                    {{ city.alias }}
                  </v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>
            </template>
          </v-list>
        </v-flex>

        <v-flex
          class="xs12 sm6 md3"
          :class="{ 'mt-4': $vuetify.breakpoint.xsOnly }"
        >
          <v-list dense>
            <v-subheader
              class="subheading font-weight-bold"
              style="height: 25px"
            >
              {{ $t('footer.knowMore') }}
            </v-subheader>
            <v-list-tile
              :to="{ path: faqPath }"
              :class="{ 'ml-2': $vuetify.breakpoint.smAndDown }"
            >
              <v-list-tile-content>
                <v-list-tile-title>
                  {{ $t('footer.faqs') }}
                </v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile
              :to="{ path: $t('urlGodfahterPage') }"
              :class="{ 'ml-2': $vuetify.breakpoint.smAndDown }"
            >
              <v-list-tile-content>
                <v-list-tile-title>
                  {{ $t('footer.godfather') }}
                </v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile
              href="https://parkingmanagers.com"
              target="_blank"
              :class="{ 'ml-2': $vuetify.breakpoint.smAndDown }"
            >
              <v-list-tile-content>
                <v-list-tile-title>
                  {{ $t('footer.addParking') }}
                </v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile
              href="https://developers.wesmartpark.com"
              target="_blank"
              :class="{ 'ml-2': $vuetify.breakpoint.smAndDown }"
            >
              <v-list-tile-content>
                <v-list-tile-title>
                  {{ $t('footer.developers') }}
                </v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
          </v-list>
        </v-flex>

        <v-flex
          class="xs12 sm6 md4 lg3"
          :class="{ 'mt-4': $vuetify.breakpoint.xsOnly }"
          v-if="headquarter"
        >
          <v-list two-line dense class="address-list">
            <v-subheader
              class="subheading font-weight-bold"
              style="height: 25px"
            >
              {{ $t('footer.contact') }}
            </v-subheader>
            <v-list-tile
              :href="`${headquarter.addressLink}`"
              target="_blank"
              :class="{ 'ml-2': $vuetify.breakpoint.smAndDown }"
            >
              <v-list-tile-content>
                <v-list-tile-title>{{
                  headquarter.address1
                }}</v-list-tile-title>
                <v-list-tile-sub-title>{{
                  headquarter.address2
                }}</v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>

            <v-list-tile
              :href="`mailto:${headquarter.email}`"
              :class="{ 'ml-2': $vuetify.breakpoint.smAndDown }"
            >
              <v-list-tile-content>
                <v-list-tile-title>{{ headquarter.email }}</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile
              :href="`tel:${phoneLink}`"
              :class="{ 'ml-2': $vuetify.breakpoint.smAndDown, 'chilleSchedule': isChile }"
            >
              <v-list-tile-content>
                <v-list-tile-sub-title>
                  <span v-html="headquarter.phoneDescription"></span>
                </v-list-tile-sub-title>
                <v-list-tile-title>{{ headquarter.phone }}</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>

            <template v-if="isChile">
              <v-list-tile
                :href="`https://api.whatsapp.com/send?phone=${phoneEmergencyLinkChile}`"
                target="_blank"
                v-if="phoneEmergencyLink"
                :class="{ 'ml-2': $vuetify.breakpoint.smAndDown }"
              >
                <v-list-tile-content class="mt-3">
                  <v-list-tile-sub-title>
                    {{ headquarter.phoneEmergencyDescription }}
                  </v-list-tile-sub-title>
                  <v-list-tile-title>whatsApp</v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>
            </template>
            <template v-else>
              <v-list-tile
                :href="`tel:${phoneEmergencyLink}`"
                v-if="phoneEmergencyLink"
                :class="{ 'ml-2': $vuetify.breakpoint.smAndDown }"
              >
                <v-list-tile-content class="mt-3">
                  <v-list-tile-sub-title>
                    {{ headquarter.phoneEmergencyDescription }}
                  </v-list-tile-sub-title>
                  <v-list-tile-title>{{
                    headquarter.phoneEmergency
                  }}</v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>
            </template>
          </v-list>
        </v-flex>

        <v-flex
          class="xs12 sm6 md2"
          :class="{ 'mt-4': $vuetify.breakpoint.xsOnly }"
        >
          <change-language-dropdown
            onlyText
            showIcon
            withBorder
            class="mt-2 mb-2 ml-2"
            color="rgba(0,0,0,0.54)"
          ></change-language-dropdown>

          <div class="mt-3 mb-4" style="margin-left: 8px">
            <v-btn
              :href="facebook"
              icon
              small
              class="grey--text text--darken-1 ma-0"
              target="_blank"
            >
              <font-awesome-icon size="xl" icon="fa-brands fa-facebook" />
            </v-btn>
            <v-btn
              :href="youtube"
              icon
              small
              class="grey--text text--darken-1 ma-0"
              target="_blank"
            >
              <font-awesome-icon size="xl" icon="fa-brands fa-youtube" />
            </v-btn>
            <v-btn
              :href="twitter"
              icon
              small
              class="grey--text text--darken-1 ma-0"
              target="_blank"
            >
              <font-awesome-icon size="xl" icon="fa-brands fa-x-twitter" />
            </v-btn>
            <v-btn
              :href="linkedin"
              icon
              small
              class="grey--text text--darken-1 ma-0"
              target="_blank"
            >
              <font-awesome-icon size="xl" icon="fa-brands fa-linkedin-in" />
            </v-btn>
            <v-btn
              :href="instagram"
              icon
              small
              class="grey--text text--darken-1 ma-0"
              target="_blank"
            >
              <font-awesome-icon size="xl" icon="fa-brands fa-instagram" />
            </v-btn>
          </div>

          <span class="caption theme--light v-subheader" style="height: auto">
            {{ $t('footer.downloadApp') }}
          </span>
          <div style="margin-left: 10px">
            <v-btn
              :href="android"
              icon
              small
              class="grey--text text--darken-1 ma-0"
              target="_blank"
            >
              <font-awesome-icon size="xl" icon="fa-brands fa-android" />
            </v-btn>
            <v-btn
              :href="apple"
              icon
              small
              class="grey--text text--darken-1 ma-0"
              target="_blank"
            >
              <font-awesome-icon size="xl" icon="fa-brands fa-apple" />
            </v-btn>
            <v-btn
              :href="huawei"
              icon
              small
              class="grey--text text--darken-1 ma-0"
              target="_blank"
            >
              <img width="80%" src="/image/huawei-appgallery-icon.png" />
            </v-btn>
          </div>
        </v-flex>
      </v-layout>

      <v-layout
        row
        wrap
        justify-center
        class="text-xs-center mt-3"
        style="font-size: 11px"
      >
        <v-flex class="xs12 md4">
          <div>&copy;{{ year }} — wesmartPark. All rights reserved</div>
          <div>
            <a :href="$t('urlTermsOfUse')" target="_blank">
              {{ $t('footer.useTerm') }}
            </a>
            |
            <a :href="$t('urlPrivacyPolicy')" target="_blank">
              {{ $t('footer.privacity') }}</a
            >
          </div>
          <!-- <div>made with &#10084; from BCN</div> -->
        </v-flex>
      </v-layout>
    </v-container>
  </v-footer>
</template>

<script>
import store from '@/store';
import ChangeLanguageDropdown from '@/components/commons/ChangeLanguageDropdown';
import _ from 'lodash';
import utilService from '@/services/utilService';

const configFile = require(process.env.VUE_APP_CONFIG);
const config = configFile.config;

export default {
  name: 'WspFooter',
  components: {
    [ChangeLanguageDropdown.name]: ChangeLanguageDropdown,
  },
  computed: {
    faqPath() {
      if (store.state.isLogged) {
        return this.$i18n.t('urlFaq');
      } else {
        return this.$i18n.t('urlFaqPage');
      }
    },
    cities() {
      return store.state.citiesArray;
    },
    countrySelected() {
      return store.state.countrySelected;
    },
    headquarter() {
      let hqs = _.find(store.state.headquarters, function (o) {
        return (
          o.city.toLowerCase() === store.state.citySelected.alias.toLowerCase()
        );
      });
      if (typeof hqs === 'undefined') {
        return {
          city: '',
          address1: '',
          address2: '',
          addressLink: '',
          phone: '',
          phoneDescription: '',
          phoneEmergency: '',
          phoneEmergencyDescription: '',
          email: '',
        };
      }

      return hqs;
    },
    year() {
      return new Date().getFullYear();
    },
    phoneLink() {
      if (this.headquarter === null) {
        return '';
      }
      return this.headquarter.phone
        .replace(/\s/g, '')
        .replace('(', '')
        .replace(')', '');
    },
    phoneEmergencyLink() {
      if (this.headquarter === null) {
        return '';
      }
      return this.headquarter.phoneEmergency
        .replace(/\s/g, '')
        .replace('(', '')
        .replace(')', '');
    },
    phoneEmergencyLinkChile() {
      if (this.headquarter === null) {
        return '';
      }
      return this.headquarter.phoneEmergency
        .replace(/\s/g, '')
        .replace('(', '')
        .replace(')', '');
    },
    facebook() {
      return config.SITE[store.state.countrySelected.code].SOCIAL.FACEBOOK;
    },
    youtube() {
      return config.SITE[store.state.countrySelected.code].SOCIAL.YOUTUBE;
    },
    twitter() {
      return config.SITE[store.state.countrySelected.code].SOCIAL.TWITTER;
    },
    linkedin() {
      return config.SITE[store.state.countrySelected.code].SOCIAL.LINDEKIN;
    },
    instagram() {
      return config.SITE[store.state.countrySelected.code].SOCIAL.INSTAGRAM;
    },
    android() {
      return config.SITE[store.state.countrySelected.code].APP.ANDROID;
    },
    apple() {
      return config.SITE[store.state.countrySelected.code].APP.APPLE;
    },
    huawei() {
      return 'https://appgallery.cloud.huawei.com/ag/n/app/C101270523?channelId=site&id=163e069517e04686b02f8d00b7973422&s=02CA11903D7DF1B04BF3D189381E0FDC71058552C1D62C27E9F412F0C1F9A8F8&detailType=0&v=';
    },
    isChile() {
      return utilService.isChile();
    },
  },
  data() {
    return {};
  },
  methods: {
    disableCity(cityCode) {
      return (
        store.state.citySelected.code.toLowerCase() === cityCode.toLowerCase()
      );
    },
  },
};
</script>

<style lang="scss">
#footer .v-list--dense:not(.address-list) .v-list__tile {
  height: 22px !important;
}
#footer .v-list--dense .v-list__tile {
  height: 40px !important;
}
#footer .v-list--dense .chilleSchedule .v-list__tile {
  height: 60px !important;
}
#footer .v-list__tile__action {
  min-width: 30px;
}
#footer a {
  color: inherit;
  text-decoration: inherit;
}
footer {
  height: auto !important;
  background-color: white !important;
}
</style>
